// Cached user profile for offline cold-start.
//
// On every successful /api/me the resolved profile is stashed in the 'user'
// store with a cachedAt timestamp. On cold start with a token, useAuth
// hydrates from this row immediately so the app can paint without waiting
// for the network. The id field is the keyPath; we only ever keep one row,
// keyed by 'me' so reads don't depend on knowing the server id ahead of
// time.

import { clear, del, get, getAll, put } from './db';

const KEY = 'me';
// Rows older than this are treated as "stale": still good enough to hydrate
// with offline, but the auth layer should re-validate before extending trust.
const STALENESS_MS = 30 * 24 * 60 * 60 * 1000;

export async function getCachedUser() {
  const row = await get('user', KEY);
  if (!row) return null;
  return {
    user: row.user,
    cachedAt: row.cachedAt,
    isStale: Date.now() - new Date(row.cachedAt).getTime() > STALENESS_MS,
  };
}

export async function putCachedUser(user) {
  await put('user', { id: KEY, user, cachedAt: new Date().toISOString() });
}

export async function clearCachedUser() {
  await del('user', KEY);
}

// Wipe every offline store. Called on logout so a sign-out actually clears
// the device — otherwise the next login on the same device would see the
// previous user's habits/completions/queue on first paint.
export async function clearAllOfflineData() {
  await Promise.all([
    clear('habits'),
    clear('completions'),
    clear('sickWeeks'),
    clear('user'),
    clear('outbox'),
    clear('meta'),
  ]);
}

// Diagnostic only — not used in normal flows.
export async function listAllUsers() {
  return getAll('user');
}
